import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import RecommendIcon from '@mui/icons-material/HowToVote';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import { FProcessStageBar, FWorkbenchShell } from '../workbench';
import { FGrid } from '../grid';
import { PageProps } from '../../../components/types/coretypes';
import ExecutiveReviewCards from './ExecutiveReviewCards';
import ExecutiveReviewManager from './ExecutiveReviewManager';
import ExecutiveReviewTabs from './ExecutiveReviewTabs';
import ScenarioComparisonGrid from './ScenarioComparisonGrid';
import ExecutiveReadinessPanel from './ExecutiveReadinessPanel';
import {
  fetchExecutiveReviewManager,
  fetchExecutiveReviewWorkbench,
  initializeDeterminantBudget,
  recommendExecutiveScenario,
  returnExecutiveReview,
  selectExecutiveScenario,
} from './executiveReview.apis';
import {
  ExecutiveReviewTabCode,
  IExecutiveReviewManager,
  IExecutiveReviewManagerRow,
  IExecutiveReviewWorkbench,
  IExecutiveScenarioCandidate,
} from './executiveReview.models';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import FPlanningJourney from '../core/FPlanningJourney';
type State = {
  activeTab: ExecutiveReviewTabCode;
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  manager: IExecutiveReviewManager | null;
  selectedReviewId: number | null;
  workbench: IExecutiveReviewWorkbench | null;
  reason: string;
  conditions: string;
  returnCategory: string;
  returnReason: string;
};
class ExecutiveReviewWorkbench extends React.Component<PlanningPageProps, State> {
  state: State = {
    activeTab: 'summary',
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    manager: null,
    selectedReviewId: null,
    workbench: null,
    reason: '',
    conditions: '',
    returnCategory: 'ASSUMPTIONS',
    returnReason: '',
  };

  componentDidMount() {
    this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    // const p = (this.props.params || {}) as any;
    // return Number(p.planningcycleid ?? p.pci ?? p.planningCycleId ?? 0);
    const value = this.props.plancontext?.pci;
    return Number(value || 0);
  };

  private initialize = async () => {
      const planningcycleid = this.getPlanningCycleId();
  
      if (!planningcycleid) {
        this.setState({
          error: 'Planning Cycle context is missing. Please set your preferred context in the Planning Setup ',
        });
        return;
      }
  
      this.setState({ loading: true, error: '', success: '' });
  
      try {
        const [phases] = await Promise.all([fetchPlanningJourneyPhases()]);
        this.setState({ phases });
        await this.loadManager();
      } catch (error: any) {
        this.setState({ error: error?.message || String(error) });
      } finally {
        this.setState({ loading: false });
      }
    };

  private loadManager = async (preserve = false) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Please set your preferred context in Planning Setup.',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: preserve ? this.state.success : '',
      warning: '',
    });

    try {
      const manager = await fetchExecutiveReviewManager(planningcycleid);
      this.setState({ manager, workbench: null });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private openReview = async (row: IExecutiveReviewManagerRow) => {
    this.setState(
      {
        selectedReviewId: row.reviewid,
        workbench: null,
        activeTab: 'summary',
        error: '',
        success: '',
        warning: '',
      },
      () => this.getWorkbench()
    );
  };

  private returnToManager = async () => {
    this.setState(
      {
        selectedReviewId: null,
        workbench: null,
        activeTab: 'summary',
        reason: '',
        conditions: '',
        returnReason: '',
        error: '',
        success: '',
        warning: '',
      },
      () => this.loadManager()
    );
  };

  getWorkbench = async (preserve = false) => {
    const planningcycleid = this.getPlanningCycleId();
    const reviewid = this.state.selectedReviewId;

    // No selected dossier means the Manager is the current workspace.
    if (!reviewid) {
      await this.loadManager(preserve);
      return;
    }

    this.setState({
      loading: true,
      error: '',
      success: preserve ? this.state.success : '',
      warning: '',
    });

    try {
      const w = await fetchExecutiveReviewWorkbench(planningcycleid, reviewid);
      this.setState({
        workbench: w,
        activeTab: this.state.workbench
          ? this.state.activeTab
          : w.activeTab || 'summary',
        reason: w.recommendation?.reason || '',
        conditions: w.recommendation?.conditions || '',
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private mutate = async (message: string, fn: () => Promise<any>) => {
    this.setState({ loading: true, error: '', success: '', warning: '' });
    try {
      await fn();
      this.setState({ success: message });
      await this.getWorkbench(true);
    } catch (e: any) {
      this.setState({ error: e?.message || String(e), loading: false });
    }
  };

  selectScenario = async (row: IExecutiveScenarioCandidate) => {
    const w = this.state.workbench!;
    await this.mutate('Scenario selected for executive consideration.', () =>
      selectExecutiveScenario(
        w.context.executivereviewid,
        w.context.simulationsubmissionid,
        row.scenarioid,
        row.simulationresultid
      )
    );
  };

  recommend = async () => {
    const w = this.state.workbench!;
    const row = w.scenarios.find((x) => x.scenarioid === w.selectedscenarioid);
    if (!row) throw new Error('Select an eligible scenario.');
    if (!this.state.reason.trim()) {
      this.setState({
        warning: 'Executive recommendation rationale is required.',
      });
      return;
    }
    await this.mutate('Scenario formally recommended.', () =>
      recommendExecutiveScenario({
        planningcycleid: w.context.planningcycleid,
        executivereviewid: w.context.executivereviewid,
        simulationsubmissionid: w.context.simulationsubmissionid,
        scenarioid: row.scenarioid,
        simulationresultid: row.simulationresultid,
        reason: this.state.reason.trim(),
        conditions: this.state.conditions.trim(),
      })
    );
  };

  returnForRevision = async () => {
    const w = this.state.workbench!;
    if (!this.state.returnReason.trim()) {
      this.setState({ warning: 'Return reason is required.' });
      return;
    }
    await this.mutate('Simulation returned for revision.', () =>
      returnExecutiveReview({
        executivereviewid: w.context.executivereviewid,
        simulationsubmissionid: w.context.simulationsubmissionid,
        categorycode: this.state.returnCategory,
        reason: this.state.returnReason.trim(),
      })
    );
  };

  continueToBudget = async () => {
    const w = this.state.workbench!;
    const r = await initializeDeterminantBudget(w.context.planningcycleid, w.context.executivereviewid);
    const set = this.props.onParam('set');
    set('planningcycleid', w.context.planningcycleid);
    if (w.context.planningcycleix)
      set('planningcycleix', w.context.planningcycleix);
    set('executivereviewid', w.context.executivereviewid);
    if (w.context.executivereviewix)
      set('executivereviewix', w.context.executivereviewix);
    if (w.recommendation?.recommendationix)
      set('recommendationix', w.recommendation.recommendationix);
    if (r?.rid) set('determinantbudgetid', r.rid);
    if (r?.rix) set('determinantbudgetix', r.rix);
    set('planningcyclephase', 'DETERMINANT_BUDGET');
    this.props.setPage(w.actions.nextpage || 'determinantbudget');
  };

  renderContext() {
    const c = this.state.workbench?.context;
    if (!c) return null;
    return (
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption">Planning Cycle</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.planningcyclename || c.planningcyclecode}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption">Executive Review</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {c.executivereviewix || `#${c.executivereviewid}`}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Chip size="small" label={c.statecode} />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={c.fiscalyear || 'Current period'}
          />
        </Grid>
      </Grid>
    );
  }

  renderTab() {
    const w = this.state.workbench;
    if (!w) return null;
    const s = w.scenarios.find((x) => x.scenarioid === w.selectedscenarioid);
    switch (this.state.activeTab) {
      case 'comparison':
        return (
          <ScenarioComparisonGrid
            rows={w.scenarios}
            selectedScenarioId={w.selectedscenarioid}
            loading={this.state.loading}
            editable={w.actions.canSelectScenario}
            onSelectScenario={this.selectScenario}
          />
        );
      case 'capacity':
        return (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Financial Capacity</Typography>
            <Typography>
              Selected Scenario: <b>{s?.scenariocode || 'None'}</b>
            </Typography>
            <Typography>Trade Surplus: {s?.partialresult ?? '-'}</Typography>
            <Typography>
              Economic Capacity: {s?.economiccapacity ?? '-'}
            </Typography>
            <Typography>
              Available Budget: {s?.availablebudget ?? '-'}
            </Typography>
          </Paper>
        );
      case 'risk':
        return (
          <Stack spacing={2}>
            <Alert severity={s?.stresscode === 'HIGH' ? 'error' : 'info'}>
              Stress classification: {s?.stresscode || '-'}
            </Alert>
            <Typography>
              Sensitivity score: {s?.sensitivityscore ?? '-'}
            </Typography>
            <Typography>Objective gap: {s?.objectivegap ?? '-'}</Typography>
          </Stack>
        );
      case 'recommendation':
        return (
          <Stack spacing={2}>
            <ExecutiveReadinessPanel readiness={w.readiness} />
            <TextField
              label="Executive Recommendation Rationale"
              value={this.state.reason}
              onChange={(e) => this.setState({ reason: e.target.value })}
              multiline
              minRows={4}
              fullWidth
            />
            <TextField
              label="Conditions / Reservations"
              value={this.state.conditions}
              onChange={(e) => this.setState({ conditions: e.target.value })}
              multiline
              minRows={3}
              fullWidth
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<RecommendIcon />}
                disabled={!w.actions.canRecommend || this.state.loading}
                onClick={this.recommend}
              >
                Recommend Scenario
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                disabled={
                  !w.actions.canInitializeDeterminantBudget ||
                  this.state.loading
                }
                onClick={this.continueToBudget}
              >
                Continue to Determinant Budget
              </Button>
            </Stack>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Return for Revision
              </Typography>
              <TextField
                select
                size="small"
                label="Return Category"
                value={this.state.returnCategory}
                onChange={(e) =>
                  this.setState({ returnCategory: e.target.value })
                }
                sx={{ minWidth: 260, my: 1 }}
              >
                <MenuItem value="ASSUMPTIONS">Assumptions</MenuItem>
                <MenuItem value="DRIVERS">Drivers</MenuItem>
                <MenuItem value="SCENARIO">Scenario Composition</MenuItem>
                <MenuItem value="SENSITIVITY">Sensitivity Analysis</MenuItem>
                <MenuItem value="COMPUTATION">Computation Issue</MenuItem>
                <MenuItem value="DOCUMENTATION">Documentation</MenuItem>
              </TextField>
              <TextField
                label="Return Reason"
                value={this.state.returnReason}
                onChange={(e) =>
                  this.setState({ returnReason: e.target.value })
                }
                multiline
                minRows={3}
                fullWidth
              />
              <Button
                sx={{ mt: 1 }}
                variant="outlined"
                color="warning"
                startIcon={<ReplayIcon />}
                disabled={!w.actions.canReturn || this.state.loading}
                onClick={this.returnForRevision}
              >
                Return for Revision
              </Button>
            </Paper>
          </Stack>
        );
      case 'history':
        return (
          <FGrid
            title="Decision History"
            rows={w.history || []}
            columns={[
              { field: 'created', headerName: 'Date', width: 180 },
              { field: 'actionname', headerName: 'Action', width: 200 },
              { field: 'statecode', headerName: 'State', width: 150 },
              { field: 'actorname', headerName: 'Actor', width: 180 },
              { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 260 },
            ]}
            getRowId={(r: any) => r.id}
            height={430}
          />
        );
      default:
        return (
          <Stack spacing={2}>
            <ExecutiveReviewCards summary={w.summary} />
            <Alert severity="info">
              Executive Review compares the frozen Simulation candidates and
              recommends exactly one scenario as the basis for Determinant
              Budget.
            </Alert>
            {s && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Selected Scenario
                </Typography>
                <Typography>
                  {s.scenariocode} — {s.scenarioname}
                </Typography>
              </Paper>
            )}
          </Stack>
        );
    }
  }

  render() {
    const w = this.state.workbench;
    const inReview = Boolean(this.state.selectedReviewId && w);
    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={inReview ? this.returnToManager : this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            //currentPhaseCode={w?.context.phasecode || 'EXECUTIVE_REVIEW'}
            currentPhaseCode={'EXECUTIVE_REVIEW'}
          />
        }
        
        title="BIE Executive Review"
        subtitle="Executive recommendation of one Simulation scenario for Determinant Budget"
        icon={<GroupsIcon />}
        loading={this.state.loading}
        onRefresh={inReview ? this.getWorkbench : this.loadManager}
        summary={this.renderContext()}
      >
        <Stack spacing={2}>
          <FProcessStageBar
            activeCode="EXECUTIVE_REVIEW"
            stages={[
              { code: 'HISTORICAL', label: 'Historical' },
              { code: 'FORECASTING', label: 'Forecasting' },
              { code: 'SIMULATION', label: 'Simulation' },
              { code: 'EXECUTIVE_REVIEW', label: 'Executive Review' },
              { code: 'DETERMINANT_BUDGET', label: 'Determinant Budget' },
              { code: 'PLANNING', label: 'Planning' },
            ]}
          />
          {this.state.error && (
            <Alert severity="error">{this.state.error}</Alert>
          )}
          {this.state.success && (
            <Alert severity="success">{this.state.success}</Alert>
          )}
          {this.state.warning && (
            <Alert severity="warning">{this.state.warning}</Alert>
          )}
          {!inReview && this.state.manager && (
            <ExecutiveReviewManager
              manager={this.state.manager}
              loading={this.state.loading}
              onOpenReview={this.openReview}
            />
          )}
          {inReview && w?.readiness.stale && (
            <Alert severity="warning">
              The submitted Simulation package is stale or superseded.
              Recommendation is blocked.
            </Alert>
          )}
          {inReview && w && (
            <ExecutiveReviewTabs
              value={this.state.activeTab}
              blockingcount={w.readiness.blockingcount}
              onChange={(activeTab) => this.setState({ activeTab })}
            />
          )}
          {inReview && <Box>{this.renderTab()}</Box>}
        </Stack>
      </PlanningWorkbenchShell>
    );
  }
}
export default ExecutiveReviewWorkbench;
